import { useState, useRef, useEffect, useCallback } from "react";
import { Row, Col, Table, Input, Select, DatePicker, Modal, Tag, Form, Popover } from "antd";
import {
	LoadingOutlined,
	CloseOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { CALL_FEATCH_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserInfo, getUserList } from "redux/selectors";
import COLORS from "common/Colors";
import dayjs from "dayjs";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import TransactionViewModal from "pages/Accounts/TransactionViewModal";
import BillingIcon from "assets/billdesicon.svg";

const { Option } = Select;

// ─── Styled Components ─────────────────────────────────────────────────────
const PageWrap = styled.div`
  ${'' /* background: #f8f8f8; */}
  min-height: 100vh;
  padding: 28px 0px;

  .filter-input {
    min-width: 160px;
    max-width: 210px;
  }
  .filter-input.ant-input-affix-wrapper {
    border-radius: 20px !important;
    height: 38px !important;
    display: flex;
    align-items: center;
    padding: 0 11px;
    border: 1px solid #d9d9d9;
  }
  .filter-input.ant-input-affix-wrapper .ant-input {
    border: none !important;
    box-shadow: none !important;
  }
  .filter-input.ant-input-affix-wrapper:hover,
  .filter-input.ant-input-affix-wrapper:focus-within {
    border-color: ${COLORS.PRIMARY} !important;
    box-shadow: none !important;
  }
  .filter-input .ant-input-prefix { margin-right: 6px; }
  .filter-input .ant-select-selector {
    border-radius: 20px !important;
    height: 38px !important;
    display: flex;
    align-items: center;
  }
  .filter-input.ant-select-focused .ant-select-selector,
  .filter-input .ant-select-selector:hover {
    border-color: ${COLORS.PRIMARY} !important;
    box-shadow: none !important;
  }
  .ant-table { border-radius: 8px; }
`;

const FiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
`;

const TotalLog = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.TEXT?.PRIMARY || "#111827"};
  white-space: nowrap;
`;

const FiltersGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const DateRangePill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 20px;
  padding: 0 12px;
  height: 38px;
  transition: border-color 0.2s;
  &:focus-within { border-color: ${COLORS.PRIMARY}; }
  .sep { color: #9ca3af; font-size: 14px; flex-shrink: 0; user-select: none; }
  .ant-picker {
    border: none !important;
    box-shadow: none !important;
    padding: 0 !important;
    background: transparent !important;
    height: 100%;
    width: 100px;
  }
  .ant-picker-input > input { font-size: 13px; color: #374151; }
  .ant-picker-suffix { color: #9ca3af; font-size: 13px; }
`;

const ActionByCell = styled.div`
  .name { font-weight: 500; font-size: 13px; color: #111827; }
  .time { font-size: 12px; color: #9ca3af; margin-top: 2px; }
`;

const LinkText = styled.a`
  font-size: 14px;
  color: #2196f3;
  text-decoration: none;
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const MoneyText = styled.span`
  font-size: 14px;
  color: ${({ $muted }) => ($muted ? "#9ca3af" : "#111827")};
  font-weight: ${({ $muted }) => ($muted ? 400 : 500)};
`;

// ─── Modal Styles ──────────────────────────────────────────────────────────
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 650px;
  padding: 32px 36px 36px;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0,0,0,0.18);
`;

const ModalCloseBtn = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 18px;
  line-height: 1;
  padding: 4px;
  border-radius: 4px;
  &:hover { color: #374151; background: #f3f4f6; }
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 24px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FRCode = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #2196f3;
`;

const ModalBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const MetaInfoRow = styled.div`
  display: flex;
  ${'' /* flex-wrap: wrap; */}
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 18px;
`;

const MetaBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 400;
  background: ${({ $variant }) => $variant === "approved" ? "#e6f4f0" : "#f0f0f0"};
  color: ${({ $variant }) => $variant === "approved" ? "#15803d" : "#374151"};
`;

const Badge = styled.span`
  background: ${({ $bg }) => $bg || "#f3f4f6"};
  color: ${({ $color }) => $color || "#374151"};
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: ${({ $border }) => $border || "none"};
`;

const PaidDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  display: inline-block;
`;

const SendBtn = styled.button`
  background: #fff;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  padding: 4px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  &:hover { border-color: #9ca3af; background: #f9fafb; }
`;

const ModalField = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
  &:last-of-type { border-bottom: none; }
`;

const FieldLabel = styled.div`
  width: 180px;
  flex-shrink: 0;
  font-size: 14px;
  color: #6b7280;
`;

const FieldValue = styled.div`
  font-size: 14px;
  color: #111827;
  font-weight: 450;
  flex: 1;
`;

const DescriptionBlock = styled.div`
  padding: 8px 0 4px;
  margin-top: 4px;
  word-break: break-all;
`;

const DescriptionLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 12px;
  font-weight: 400;
`;

const DescriptionText = styled.div`
  font-size: 14px;
  color: #111827;
  line-height: 1.7;
  word-break: break-all;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const Divider = styled.div`
  height: 1px;
  background: #f3f4f6;
  margin: 16px 0;
`;

const StyledTable = styled(Table)`
  padding-top: 10px;
  table {
    width: 100% !important;
    border: 1px solid #ccc;
  }
  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
    color: ${COLORS.TEXT.PRIMARY};
    font-size: 14px;
  }
  .ant-table-row:nth-child(even) {
    background-color: #fafafa;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    color: ${COLORS.TEXT.PRIMARY};
    white-space: nowrap;
    padding: 12px 16px !important;
    height: 48px !important;
  }
  .ant-table-tbody .ant-table-cell {
    padding: 12px 16px !important;
    height: 48px !important;
  }
  .ant-select-selector {
    min-width: 150px;
  }
`;

// ─── API Endpoints ─────────────────────────────────────────────────────────
const API = {
	GET_TRANSACTIONS: "insyts-settings/transactions",
	GET_FILTER_LIST: "insyts-settings/transactionfilterlist",
};

const PAGE_SIZE = 10;

// ─── Normalise ─────────────────────────────────────────────────────────────
const normaliseTransaction = (item, index) => {
	return {
		key: item.transactionid || String(index),
		by: item.createdby || "—",
		time: item.dateoftransaction
			? dayjs(item.dateoftransaction).format("DD MMM YYYY, hh:mm A")
			: item.createdat
				? dayjs(item.createdat).format("DD MMM YYYY, hh:mm A")
				: "—",
		category: item.category || "—",
		source: item.sourcename || "—",
		destination: item.destinationname || "—",
		fundOut:
			item.fundout != null
				? `${item.currency || ""} ${item.fundout}`.trim()
				: "---",
		fundIn:
			item.fundin != null
				? `${item.currency || ""} ${item.fundin}`.trim()
				: "---",
		balance:
			item.fundbalance != null
				? `${item.currency || ""} ${item.fundbalance}`.trim()
				: "—",
		matterfundbalance: item.matterfundbalance != null
			? `${item.currency || ""} ${item.matterfundbalance}`.trim()
			: "—",

		// keep raw fields for modal
		_raw: item,
	};
};

// ─── Helper ────────────────────────────────────────────────────────────────
const fmt = (val) => (!val || val === "---" || val === "—" ? "———" : val);
const fmtDate = (iso) =>
	iso ? dayjs(iso).format("Do MMM YYYY") : "———";

// ─── Modal Content: Fund Added ─────────────────────────────────────────────
const FundAddedModal = ({ raw }) => {
	const amount = raw.fundin ?? raw.fundout;
	return (
		<>
			<ModalTitle>Fund Added</ModalTitle>
			<ModalField><FieldLabel>Client</FieldLabel>           <FieldValue>{fmt(raw.clientname)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Matter</FieldLabel>           <FieldValue>{fmt(raw.mattername)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Date of Transaction</FieldLabel><FieldValue>{fmtDate(raw.dateoftransaction)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Amount</FieldLabel>           <FieldValue>{amount != null ? `${raw.currency || ""} ${amount}`.trim() : "———"}</FieldValue></ModalField>
			<ModalField><FieldLabel>Source</FieldLabel>           <FieldValue>{fmt(raw.sourcename)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Destination</FieldLabel>      <FieldValue>{fmt(raw.destinationname)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Payment Method</FieldLabel>   <FieldValue>{fmt(raw.paymentmethod)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Other Remarks</FieldLabel>    <FieldValue>{fmt(raw.otherremarks)}</FieldValue></ModalField>
			{raw.description && (
				<DescriptionBlock>
					<div className="desc-label">Description</div>
					<div className="desc-text">{raw.description}</div>
				</DescriptionBlock>
			)}
		</>
	);
};

// ─── Modal Content: Fund Request ───────────────────────────────────────────
const FundRequestModal = ({ raw, getUserName }) => {
	const amount = raw.fundrequestamount ?? raw.fundrequestamount;
	return (
		<>
			<ModalTitle>
				Fund Request{raw.fundrequestid && <FRCode>({raw.fundrequestid})</FRCode>}
			</ModalTitle>

			<MetaInfoRow>
				{raw.createdby && (
					<MetaBadge>
						<span style={{ color: "#6b7280", fontWeight: 600 }}>
							Created by:
						</span>

						<span style={{ color: "#374151" }}>
							{getUserName(raw.createdby)}
						</span>

						{raw.createdat && (
							<span style={{ color: "#6b7280" }}>
								; {dayjs(raw.createdat).format("DD MMM YYYY")}
							</span>
						)}
					</MetaBadge>
				)}

				{raw.fundrequestapprovedby && (
					<MetaBadge $variant="approved">
						<span style={{ color: "#03d34f", fontWeight: 600 }}>
							Approved by:
						</span>

						<span style={{ color: "#15803d" }}>
							{getUserName(raw.fundrequestapprovedby)}
						</span>

						{raw.fundrequestcreatedat && (
							<span style={{ color: "#15803d" }}>
								; {dayjs(raw.fundrequestcreatedat).format("DD MMM YYYY")}
							</span>
						)}
					</MetaBadge>
				)}
			</MetaInfoRow>

			{/* Status + Send button row */}
			{/* <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
				{raw.status === "Paid" && (
					<Badge $bg="#f0fdf4" $color="#16a34a" $border="1px solid #bbf7d0">
						<PaidDot /> Paid
					</Badge>
				)}
				<SendBtn>Send</SendBtn>
			</div> */}

			<ModalField><FieldLabel>Client</FieldLabel>              <FieldValue>{fmt(raw.clientname)}</FieldValue></ModalField>
			<ModalField>
				<FieldLabel>Requesting Fund for</FieldLabel>
				{raw?.fundrequesttype === "Matter Fund" ? (
					<FieldValue>{fmt(raw.fundrequesttype || raw.sourcename)}</FieldValue>
				) : <FieldValue>{fmt(raw.fundrequesttype || raw.sourcename)}</FieldValue>}
			</ModalField>
			{raw?.fundrequesttype === "Matter Fund" && (
				<ModalField>
					<FieldLabel>Matter Name</FieldLabel>
					<FieldValue>{fmt(raw.mattername)}</FieldValue>
				</ModalField>
			)}			<ModalField><FieldLabel>Amount</FieldLabel>              <FieldValue>{amount != null ? `${raw.currency || ""} ${amount}`.trim() : "———"}</FieldValue></ModalField>
			<ModalField><FieldLabel>Due Date</FieldLabel>            <FieldValue>{fmtDate(raw.duedate || raw.dateoftransaction)}</FieldValue></ModalField>

			{raw.description && (
				<DescriptionBlock>
					<DescriptionLabel>Description</DescriptionLabel>
					<DescriptionText>{raw.description}</DescriptionText>
				</DescriptionBlock>
			)}
			{raw.reason && (
				<DescriptionBlock>
					<div className="desc-label">Reason</div>
					<div className="desc-text">{raw.reason}</div>
				</DescriptionBlock>
			)}
		</>
	);
};

// ─── Modal Content: Fund Moved ─────────────────────────────────────────────
const FundMovedModal = ({ raw }) => {
	const amount = raw.fundin ?? raw.fundout;
	return (
		<>
			<ModalTitle>Fund Moved</ModalTitle>
			<ModalField><FieldLabel>Client</FieldLabel>           <FieldValue>{fmt(raw.clientname)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Date of Movement</FieldLabel> <FieldValue>{fmtDate(raw.dateoftransaction)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Type of Movement</FieldLabel> <FieldValue>{fmt(raw.typeofmovement)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Source</FieldLabel>           <FieldValue>{fmt(raw.sourcename)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Destination</FieldLabel>      <FieldValue>{fmt(raw.destinationname)}</FieldValue></ModalField>
			<ModalField><FieldLabel>Amount Transferred</FieldLabel><FieldValue>{amount != null ? `${raw.currency || ""} ${amount}`.trim() : "———"}</FieldValue></ModalField>
			{raw.description && (
				<DescriptionBlock>
					<div className="desc-label">Description</div>
					<div className="desc-text">{raw.description}</div>
				</DescriptionBlock>
			)}
		</>
	);
};

// ─── Transaction Detail Modal (router) ────────────────────────────────────
const TransactionModal = ({ record, onClose, getUserName, clientId }) => {
	if (!record) return null;

	const category = record.category?.trim();
	const isBilling = category?.toLowerCase() === "billing";

	// ── Billing: delegate entirely to TransactionViewModal ────────────────
	if (isBilling) {
		return (
			<TransactionViewModal
				open={!!record}
				onClose={onClose}
				record={record._raw} // TransactionViewModal reads record._raw internally
			/>
		);
	}

	// ── Non-billing: existing modal UI ────────────────────────────────────
	const renderContent = () => {
		if (category === "Fund Added")
			return <FundAddedModal raw={record._raw} />;
		if (category === "Fund Request")
			return <FundRequestModal raw={record._raw} getUserName={getUserName} />;
		if (category === "Fund Moved")
			return <FundMovedModal raw={record._raw} getUserName={getUserName} />;
		// fallback
		return (
			<>
				<ModalTitle>{category}</ModalTitle>
				<pre style={{ fontSize: 12, color: "#6b7280" }}>
					{JSON.stringify(record._raw, null, 2)}
				</pre>
			</>
		);
	};

	return (
		<ModalOverlay onClick={onClose}>
			<ModalBox onClick={(e) => e.stopPropagation()}>
				<ModalCloseBtn onClick={onClose}>
					<CloseOutlined />
				</ModalCloseBtn>
				{renderContent()}
			</ModalBox>
		</ModalOverlay>
	);
};

// ─── Main Component ───────────────────────────────────────────────────────
const Transaction = ({ matterId, matterData }) => {

	const userInfo = useSelector(getUserInfo);
	const resolvedMatterId = matterId;
	const resolvedClientId = matterData?.clientId || userInfo?.clientId || "KE-2026-0015";
	const [form] = Form.useForm();
	const userList = useSelector(getUserList);

	const [isDescModalOpen, setIsDescModalOpen] = useState(false);
	const [selectedBillRecord, setSelectedBillRecord] = useState(null);

	// ── Filter state ──────────────────────────────────────────────────────
	const [search, setSearch] = useState("");
	const [selActionBy, setSelActionBy] = useState(undefined);
	const [selCategory, setSelCategory] = useState(undefined);
	const [dateFrom, setDateFrom] = useState(null);
	const [dateTo, setDateTo] = useState(null);

	// ── Pagination ────────────────────────────────────────────────────────
	const [currentPage, setCurrentPage] = useState(1);
	const [totalRecords, setTotalRecords] = useState(0);

	// ── Table data ────────────────────────────────────────────────────────
	const [tableData, setTableData] = useState([]);
	const [loadingTable, setLoadingTable] = useState(false);
	// ── Modal state ───────────────────────────────────────────────────────
	const [selectedRecord, setSelectedRecord] = useState(null);
	// ── Filter options ────────────────────────────────────────────────────
	const [filterOptions, setFilterOptions] = useState({ actionBy: [], categories: [] });
	const [loadingFilters, setLoadingFilters] = useState(false);

	const actionByOptions = filterOptions.actionBy;
	const categoryOptions = filterOptions.categories;

	const getUserName = (emailOrId) => {
		if (!emailOrId) return emailOrId;

		// Logged in user
		if (
			emailOrId === userInfo?.userid ||
			emailOrId === userInfo?.email
		) {
			return "You";
		}

		const user = (userList || []).find(
			(u) => u.userid === emailOrId || u.email === emailOrId
		);

		return user?.fullname || emailOrId;
	};

	// ── 1. Fetch filter-list ──────────────────────────────────────────────
	const fetchFilterList = async () => {
		setLoadingFilters(true);
		try {
			const resp = await CALL_FEATCH_API(
				`${API.GET_FILTER_LIST}?matterid=${resolvedMatterId}`,
				"GET"
			);
			if (resp?.response?.data) {
				const d = resp.response.data;
				setFilterOptions({
					actionBy: Array.isArray(d.createdby)
						? d.createdby
							.filter((u) => u?.userid)
							.map((u) => ({
								label: getUserName(u.userid), // ← full name
								value: u.userid,              // ← email/id sent to API
							}))
						: [],
					categories: Array.isArray(d.categories)
						? d.categories.filter(Boolean).map((c) => ({ label: c, value: c }))
						: [],
				});
			}
		} catch (err) {
			console.error("fetchFilterList error:", err);
			setFilterOptions({ actionBy: [], categories: [] });
		} finally {
			setLoadingFilters(false);
		}
	};
	// ── 2. Fetch transactions ─────────────────────────────────────────────
	const fetchTransactions = async (page = 1, overrides = {}) => {
		setLoadingTable(true);
		try {
			const actionBy = "actionBy" in overrides ? overrides.actionBy : selActionBy;
			const category = "category" in overrides ? overrides.category : selCategory;
			const from = "dateFrom" in overrides ? overrides.dateFrom : dateFrom;
			const to = "dateTo" in overrides ? overrides.dateTo : dateTo;

			const params = new URLSearchParams({
				matterid: resolvedMatterId,
				clientid: resolvedClientId,
				page: String(page),
				pagesize: String(PAGE_SIZE),
			});

			if (search) params.append("search", search);
			// Only append if there is actually a value
			if (actionBy) params.append("actionby", actionBy);
			if (category) params.append("category", category);
			if (from) params.append("datefrom", from.format("YYYY-MM-DD"));
			if (to) params.append("dateto", to.format("YYYY-MM-DD"));

			const resp = await CALL_FEATCH_API(
				`${API.GET_TRANSACTIONS}?${params.toString()}`,
				"GET"
			);

			if (resp) {
				const rawList =
					resp.response?.data ||
					resp.data ||
					resp.records ||
					resp.transactions ||
					[];
				const total =
					resp.response?.pagination?.total ||
					resp.response?.total ||
					resp.total ||
					rawList.length;

				setTableData(Array.isArray(rawList?.filter(
					item => !(item.sourcename === "Direct Payment" && item.fundout)
				)) ? rawList?.filter(
					item => !(item.sourcename === "Direct Payment" && item.fundout)
				)?.map(normaliseTransaction) : []);
				setTotalRecords(total);
				setCurrentPage(page);
			}
		} catch (err) {
			console.error("fetchTransactions error:", err);
			setTableData([]);
		} finally {
			setLoadingTable(false);
		}
	};

	const handleFilterChange = (allValuesParam) => {
		const allValues = allValuesParam || form.getFieldsValue();
		const actionByArr = allValues?.actionBy || [];
		const categoryArr = allValues?.category || [];
		const actionBy = actionByArr.length ? actionByArr[0] : undefined;
		const category = categoryArr.length ? categoryArr[0] : undefined;
		setSelActionBy(actionBy);
		setSelCategory(category);

		fetchTransactions(1, { actionBy, category });
	};

	// ── Effects ───────────────────────────────────────────────────────────
	useEffect(() => { fetchFilterList(); }, []); // eslint-disable-line
	useEffect(() => { fetchTransactions(1); }, [resolvedMatterId]); // eslint-disable-line

	// ── Handlers ──────────────────────────────────────────────────────────
	const handlePageChange = (page) => fetchTransactions(page);
	const handleCategoryClick = (record) => setSelectedRecord(record);
	const handleModalClose = () => setSelectedRecord(null);


	// ── Columns ───────────────────────────────────────────────────────────
	const columns = [
		{
			title: "Action by",
			key: "by",
			width: 180,
			render: (_, r) => (
				<ActionByCell>
					<div className="name">{getUserName(r.by)}</div>
					<div className="time">{r.time}</div>
				</ActionByCell>
			),
		},
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
			width: 210,
			render: (val, record) => {
				const billingId = record?._raw?.billingid;
				const fundRequestId = record?._raw?.fundrequestid;

				const combinedValue = billingId
					? `${val} (${billingId})`
					: fundRequestId
						? `${val} (${fundRequestId})`
						: val;

				return (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
						}}
					>
						<LinkText onClick={() => handleCategoryClick(record)}>
							{combinedValue}
						</LinkText>

						<img
							src={BillingIcon}
							alt="Description"
							style={{ width: 16, height: 16, cursor: "pointer" }}
							onMouseEnter={(e) => {
								e.stopPropagation();
								setSelectedBillRecord(record);
								setIsDescModalOpen(true);
							}}
						/>

					</div>
				);
			},
		},
		{
			title: "Source",
			dataIndex: "source",
			key: "source",
			width: 190,
			render: (val) => <span style={{ fontSize: 14, color: "#444" }}>{val}</span>,
		},
		{
			title: "Destination",
			dataIndex: "destination",
			key: "destination",
			width: 180,
			render: (val) => <span style={{ fontSize: 14, color: "#444" }}>{val}</span>,
		},
		{
			title: "Fund Out",
			dataIndex: "fundOut",
			key: "fundOut",
			width: 110,
			align: "right",
			render: (val) => <MoneyText $muted={val === "---"}>{val}</MoneyText>,
		},
		{
			title: "Fund In",
			dataIndex: "fundIn",
			key: "fundIn",
			width: 110,
			align: "right",
			render: (val) => <MoneyText $muted={val === "---"}>{val}</MoneyText>,
		},
		{
			title: "Allocated Fund Balance",
			dataIndex: "matterfundbalance",
			key: "matterfundbalance",
			width: 130,
			align: "right",
			render: (val) => <MoneyText>{val}</MoneyText>,
		},
	];

	// ── Render ────────────────────────────────────────────────────────────
	return (
		<PageWrap>
			{/* Transaction Detail Modal */}
			<TransactionModal record={selectedRecord} onClose={handleModalClose} getUserName={getUserName} />

			<Row>
				<Col span={24}>
					<FiltersHeader>
						<TotalLog>
							Total log: {loadingTable ? "…" : totalRecords}
						</TotalLog>

						<FiltersGroup>
							<Form form={form}
								layout="vertical"
								onValuesChange={(_, allValues) => handleFilterChange(allValues)}>
								<div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
									<div style={{ width: 176 }}>
										<DropdownCheckBox
											name="actionBy"
											label={loadingFilters ? "Loading…" : "Action by"}
											options={actionByOptions}
											form={form}
											disabled={loadingFilters}
										/>
									</div>
									<div style={{ width: 176 }}>
										<DropdownCheckBox
											name="category"
											label={loadingFilters ? "Loading…" : "Category"}
											options={categoryOptions}
											form={form}
											disabled={loadingFilters}
										/>
									</div>
									<div style={{ marginBottom: 12 }}>
										<DateRangePill>
											<DatePicker
												placeholder="From"
												value={dateFrom}
												onChange={(val) => {
													setDateFrom(val);
													fetchTransactions(1, { dateFrom: val });
												}}
												disabledDate={(d) => dateTo && d && d.isAfter(dateTo, "day")}
												allowClear
											/>
											<span className="sep">↔</span>
											<DatePicker
												placeholder="To"
												value={dateTo}
												onChange={(val) => {
													setDateTo(val);
													fetchTransactions(1, { dateTo: val });
												}}
												disabledDate={(d) => dateFrom && d && d.isBefore(dateFrom, "day")}
												allowClear
											/>
										</DateRangePill>
									</div>
								</div>
							</Form>
						</FiltersGroup>
					</FiltersHeader>
				</Col>

				<Col span={24}>
					<StyledTable
						columns={columns}
						dataSource={tableData}
						loading={loadingTable}
						pagination={{
							current: currentPage,
							pageSize: PAGE_SIZE,
							total: totalRecords,
							size: "small",
							onChange: handlePageChange,
							showSizeChanger: false,
						}}
						scroll={{ x: 800 }}
						size="middle"
						locale={{ emptyText: "No records found" }}
					/>
				</Col>
			</Row>
			<Modal
				title="Description"
				open={isDescModalOpen}
				onCancel={() => setIsDescModalOpen(false)}
				footer={null}
				styles={{
					content: { borderRadius: 20 },
					header: { borderRadius: "8px 8px 0 0" },
				}}
			>
				<p>{selectedBillRecord?._raw?.description || "No description available"}</p>
			</Modal>
		</PageWrap>
	);
};

export default Transaction;